import { Component, OnInit, Input, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';

import { UTMConversionService } from '../../model/utm-conversion/utm-conversion.service';
import { AccountService } from '../../model/account/account.service';
import { Account } from '../../model/account/account.model';
import { sreCategoryPinColor } from '../../utilities/heroReportPinColor';

@Component({
  selector: 'app-mapbox-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mapbox-header.component.html',
  styleUrls: ['./mapbox-header.component.scss']
})
export class MapboxHeaderComponent implements OnInit, OnChanges {
  @Input() longitude: number = 66.9237;
  @Input() latitude: number = 48.0196;
  @Input() submittedDate: Date | null = null;
  @Input() reporter: string | null = null;
  @Input() status: string = 'pending';
  @Input() classification: string | null = null;
  @Input() possibleIdentification: string[] | null = null;

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  map: mapboxgl.Map | undefined;

  constructor(private utmConversionService: UTMConversionService, private accountService: AccountService) {}

  accessToken: string = environment.mapboxAccessToken;
  account: Account;

  async ngOnInit() {
    this.account  = await this.accountService.fetch();
    this.initializeMap([this.longitude, this.latitude]);
  }

  initializeMap(center: [number, number]): void {
    this.map = new mapboxgl.Map({
      container: this.mapContainer.nativeElement,
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: center,
      zoom: 10,
      accessToken: this.accessToken,
      attributionControl: false
    });

    this.map.on('load', () => {
      this.addMarker(center);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['longitude'] || changes['latitude']) && this.map) {
      this.map.setCenter([this.longitude, this.latitude]);

      this.addMarker([this.longitude, this.latitude]);
    }
  }

  colorFromStatus(status: string): string {
    switch (status.toLocaleLowerCase()) {
      case 'completed':
        return '#41BE8A';
      case 'pending':
        return '#A23122';
      default:
        return '#A23122';
    }
  }

  backgroundColorFromStatus(status: string): string {
    switch (status.toLocaleLowerCase()) {
      case 'completed':
        return 'rgba(65, 190, 138, 0.2)';
      case 'pending':
        return 'rgba(162, 49, 34, 0.2)';
      default:
        return 'rgba(162, 49, 34, 0.2)';
    }
  }


  addMarker(coords: [number, number]): void {

    const coordinates = this.utmConversionService.useOrConvertCoordinates(
      this.latitude,
      this.longitude,
      this.account.gpsUnit
    );
    // Create a popup/tooltip
    const popup = new mapboxgl.Popup({ offset: 15, closeButton: false, closeOnClick: false })
      .setHTML(`
      <b>${this.classification}</b><br />
      ${coordinates}<br />
      <span style="font-size: 10px; padding: 3px; border-radius: 2px; color: ${this.colorFromStatus(this.status)}; background-color: ${this.backgroundColorFromStatus(this.status)}">${this.status.toUpperCase()}</span>
      `)
      .addTo(this.map!);

    // Create a marker and attach the popup
    const markerEl = document.createElement('div');
    markerEl.className = `map-marker ${this.status.toLowerCase()}`;
    // SRE categories have a fixed pin color that replaces the status color.
    const sreColor = sreCategoryPinColor(this.possibleIdentification);
    if (sreColor) {
      markerEl.style.backgroundColor = sreColor;
    }

    // 2) Create a marker with our custom element
    const marker = new mapboxgl.Marker({
      element: markerEl
    })
      .setLngLat(coords)
      .setPopup(popup)
      .addTo(this.map!);

    marker.togglePopup();
  }
}
